import classNames from 'classnames'
import Button, { ButtonType } from 'client/components/common/Button/Button'
import { ImportTypes } from 'client/components/Screen/ImportScreen/ImportScreen'
import { openImportScreen } from 'client/components/Screen/ScreenActions'
import { Analytics } from 'client/Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result } from 'client/Global/AnalyticsEvents'
import { l } from 'client/Other/Translation'
import React from 'react'
import { useDispatch } from 'react-redux'

interface State {
  file: File | null
  error: string | null
  dragOver: boolean
}

interface PickerViewProps {
  onChange: (file: File) => void
  type: ImportTypes
  close(): void
}

// https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
export const allowedTypes = [
  'text/csv', // .csv
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.oasis.opendocument.spreadsheet', // .ods
]

const PickerView: React.FC<PickerViewProps> = (props) => {
  const [state, setState] = React.useState<State>({ dragOver: false, error: null, file: null })
  const dispatch = useDispatch()

  const validateFile = () => {
    const fileSizeLimit = 5 // Mb
    let error: string | null = null

    if (state.file === null) {
      error = l('import.error.NoFile')
    } else if (allowedTypes.indexOf(state.file.type) === -1) {
      error = l('import.error.UnsupportedFormat.Body')
    } else if (state.file.size > fileSizeLimit * 1024 * 1024) {
      error = l('import.error.filesize', '', { size: String(fileSizeLimit) })
    }

    setState({
      file: error !== null ? null : state.file,
      error,
      dragOver: false,
    })

    if (error === null) {
      if (props.type === 'CUSTOMER') {
        Analytics.trackEvent(Events.importCustomers, {
          type: AnalyticsType.action,
          category: Category.customer,
          placement: Placement.customers,
          result: Result.success,
          step: 1,
          dataType: state.file ? 'csv' : 'text',
        })
      }
      if (props.type === 'PRODUCT') {
        Analytics.trackEvent(Events.importProducts, {
          type: AnalyticsType.action,
          category: Category.product,
          placement: Placement.products,
          result: Result.success,
          step: 1,
          dataType: state.file ? 'csv' : 'text',
        })
      }

      props.onChange(state.file)
      dispatch(openImportScreen(props.type, 'Mapper'))
    } else {
      if (props.type === 'CUSTOMER') {
        Analytics.trackEvent(Events.importCustomers, {
          type: AnalyticsType.action,
          category: Category.customer,
          placement: Placement.customers,
          result: Result.error,
          errorReason: error,
          step: 1,
        })
      }
      if (props.type === 'PRODUCT') {
        Analytics.trackEvent(Events.importProducts, {
          type: AnalyticsType.action,
          category: Category.product,
          placement: Placement.products,
          result: Result.error,
          errorReason: error,
          step: 1,
        })
      }
    }
  }

  return (
    <div className="picker-view">
      <h2>{l(`import.picker.Title.${props.type}`)}</h2>
      <h5>{l('import.picker.Body')}</h5>
      <div
        data-testid="drag-zone"
        className={classNames('ImportDragTarget', {
          Active: state.dragOver,
          Error: state.error,
          Picked: state.file && state.error === null,
        })}
        onDrop={(event) => {
          event.persist()
          let file: File | null = null

          // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop#process_the_drop
          if (event.dataTransfer.items) {
            const item = event.dataTransfer.items[0]
            if (item.kind === 'file') {
              file = item.getAsFile()
            }
          } else {
            file = event?.dataTransfer.files[0]
          }

          if (file !== null) {
            setState((oldState) => ({
              ...oldState,
              file,
              error: null,
            }))
          }
          event.preventDefault()
        }}
        onDragOver={(event) => {
          setState({ ...state, error: null, dragOver: true })
          event.preventDefault()
        }}
        onDragLeave={() => {
          setState({ ...state, dragOver: false })
        }}
      >
        {!state.file && (
          <React.Fragment>
            <label htmlFor="importUpload">
              <Button type={ButtonType.accept} img="/images/import-button-icon.svg" label={l('import.picker.Browse')} />
            </label>
            <input
              id="importUpload"
              style={{ display: 'none' }}
              type="file"
              accept=".csv,.xls,.xlsx,.ods"
              onChange={(event) => {
                setState((oldState) => ({
                  ...oldState,
                  file: event.target.files[0],
                  error: null,
                }))
              }}
            />
            <h5>{l('import.picker.DragDrop')}</h5>
            <p className="Error">{state.error}</p>
          </React.Fragment>
        )}
        {state.file && (
          <React.Fragment>
            <h5>{state.file.name}</h5>
            <a
              onClick={() => {
                setState({ ...state, file: null })
                props.onChange(null)
              }}
            >
              {l('import.picker.RemoveFile')}
            </a>
          </React.Fragment>
        )}
      </div>
      <p>{l('import.picker.Hint')}</p>

      <div className="Buttons">
        <Button
          label={l('import.screen.Next')}
          type={ButtonType.accept}
          action={() => {
            validateFile()
          }}
        />

        <Button
          label={l('Cancel')}
          type={ButtonType.cancel}
          action={() => {
            if (props.type === 'CUSTOMER') {
              Analytics.trackEvent(Events.importCustomers, {
                type: AnalyticsType.action,
                category: Category.customer,
                placement: Placement.customers,
                result: Result.cancel,
                step: 1,
              })
            }
            if (props.type === 'PRODUCT') {
              Analytics.trackEvent(Events.importProducts, {
                type: AnalyticsType.action,
                category: Category.product,
                placement: Placement.products,
                result: Result.cancel,
                step: 1,
              })
            }

            props.close()
          }}
        />
      </div>
    </div>
  )
}

export default PickerView
